import * as React from 'react';
import { useSelector } from 'react-redux';
import { kontodefinitionenSelector } from 'store/lookups/selector';
import { requestKontodefinitionen } from 'store/lookups/actions';
import {
    addKontodefinition,
    defaultKontodefinition,
    Kontodefinition,
    updateKontodefinition,
} from 'api/kontodefinition';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { DefaultButton, Dialog, DialogFooter, DialogType, Fabric, PrimaryButton } from '@fluentui/react';
import { viewStyles } from 'styles/viewStyles';
import { formStyles } from 'styles/formStyles';
import { FormTextField } from 'formComponents/FormTextField';
import { SavingState } from 'api/savingState';
import { BhCodeLabel, BhLandLabel } from 'utils/labels';
import { BhCode } from 'formComponents/lookups/BhCode';
import { BhLand } from 'formComponents/lookups/BhLand';

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        height: 'calc(100vh - 120px)',
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 10,
    },
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

export const Kontodefinitionen: React.FC = () => {
    const kontodefinitionen = useSelector(kontodefinitionenSelector);

    const [editData, setEditData] = React.useReducer(
        (prev: Kontodefinition | null, next: Partial<Kontodefinition> | null) => {
            if (next === null) {
                return null;
            }
            if (prev === null) {
                return { ...defaultKontodefinition, ...next };
            }
            return { ...prev, ...next };
        },
        null,
    );

    const save = async () => {
        if (!editData) {
            return;
        }
        if (editData?.recNum === -1) {
            await addKontodefinition(editData);
        } else {
            await updateKontodefinition(editData);
        }
        await requestKontodefinitionen();
        setEditData(null);
    };

    return (
        <Fabric className={styles.container}>
            <div className={styles.searchBar}>
                <div />

                <PrimaryButton
                    onClick={() => setEditData({ ...defaultKontodefinition, recNum: -1 })}
                    text="Neue Kontodefinition"
                />
            </div>
            <div className={viewStyles.listContainerScroll}>
                <table className={formStyles.lookupTable}>
                    <thead className={formStyles.lookupTableHead}>
                        <tr>
                            <td className={formStyles.lookupTableCell}>Nr</td>
                            <td className={formStyles.lookupTableCell}>BH-Code</td>
                            <td className={formStyles.lookupTableCell}>Steuercode</td>
                            <td className={formStyles.lookupTableCell}>Land (Inland/EU/EU-Ausland)</td>
                            <td className={formStyles.lookupTableCell}>Konto</td>
                            <td className={formStyles.lookupTableCell} />
                        </tr>
                    </thead>
                    <tbody className={formStyles.lookupTableBody}>
                        {kontodefinitionen
                            .sort((a, b) => a.recNum - b.recNum)
                            .map((kontodefinition) => (
                                <tr key={kontodefinition.recNum} className={formStyles.looupTableRow}>
                                    <td className={formStyles.lookupTableCell}>{kontodefinition.recNum}</td>
                                    <td className={formStyles.lookupTableCell}>
                                        {BhCodeLabel[kontodefinition.bhCode]
                                            ? BhCodeLabel[kontodefinition.bhCode]
                                            : kontodefinition.bhCode}
                                    </td>
                                    <td className={formStyles.lookupTableCell}>{kontodefinition.steuerCode}</td>
                                    <td className={formStyles.lookupTableCell}>
                                        {BhLandLabel[kontodefinition.landKz]
                                            ? BhLandLabel[kontodefinition.landKz]
                                            : kontodefinition.landKz}
                                    </td>
                                    <td className={formStyles.lookupTableCell}>{kontodefinition.konto}</td>
                                    <td className={formStyles.lookupTableActionCell}>
                                        <DefaultButton
                                            className={formStyles.lookupTableEditButton}
                                            onClick={() => setEditData({ ...kontodefinition })}
                                            text="Bearbeiten"
                                        />
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            {editData && (
                <Dialog
                    hidden={false}
                    minWidth={800}
                    dialogContentProps={{
                        title: 'Kontodefinition bearbeiten',
                        type: DialogType.normal,
                    }}
                >
                    <BhCode
                        value={editData.bhCode || ''}
                        label="BH Code"
                        menuPlacement="bottom"
                        onChange={(newValue): void => {
                            setEditData({ bhCode: newValue });
                        }}
                    />
                    <FormTextField
                        value={editData.steuerCode}
                        savingState={SavingState.notChanged}
                        options={{ numeric: true }}
                        onChange={(newValue): void => {
                            if (editData) {
                                setEditData({
                                    steuerCode: newValue as number,
                                });
                            }
                        }}
                        label="Steuercode"
                    />
                    <BhLand
                        value={editData.landKz || ''}
                        label="Inland/EU/EU-Ausland"
                        menuPlacement="bottom"
                        onChange={(newValue): void => {
                            setEditData({ landKz: newValue });
                        }}
                    />
                    <FormTextField
                        value={editData.konto}
                        savingState={SavingState.notChanged}
                        options={{ maxLength: 4 }}
                        onChange={(newValue): void => {
                            if (editData) {
                                setEditData({
                                    ...editData,
                                    konto: newValue as string,
                                });
                            }
                        }}
                        label="Konto"
                    />
                    <DialogFooter>
                        <DefaultButton onClick={() => setEditData(null)} text="Abbruch" />
                        <PrimaryButton onClick={save} text="Speichern" />
                    </DialogFooter>
                </Dialog>
            )}
        </Fabric>
    );
};
